// In-process verification for MentionParser + MentionRouter.
//
// Layered so the failure points to the broken layer:
//
//   - parser cases   (regex / ULID / @-token recognition)
//   - union cases    (server-provided list merge with locally-parsed)
//   - router cases   (one per branch of the NotificationState decision tree)
//   - integration    (parser feeding router with realistic bodies)
//
// All pure logic; no IndexedDB, no Push API, no network.
//
// Mirror of `kaether_for_ios/Stoat/E2EE/MentionsSelfTest.swift`.

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

import {
  parseMentions,
  extractUserIds,
  extractRoleIds,
  containsEveryone,
  containsHere,
  unionUserIds,
  EMPTY_PARSED_MENTIONS,
} from "./MentionParser";
import type { ParsedMentions } from "./MentionParser";

import {
  route,
  resolveTarget,
} from "./MentionRouter";
import type {
  ParsedMessage,
  CurrentUserContext,
  ChannelNotificationContext,
  NotificationDecision,
  MentionTarget,
} from "./MentionRouter";

SelfTestRunner.register({
  name: "Mentions parsing + push routing",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // Parser layer
      testParsesUserMention(),
      testParsesRoleMention(),
      testRejectsBadULID(),
      testEveryoneWordBounded(),
      testEveryoneInEmailNotMatched(),
      testParsesHere(),
      testMultipleMentionsDeduplicated(),
      testEmptyBody(),

      // Union layer
      testUnionWithServerList(),
      testUnionWithNilServerList(),

      // Router layer — one per decision-tree branch
      testSelfAuthoredSuppressed(),
      testStateNoneSuppresses(),
      testStateMentionRequiresMention(),
      testStateMentionDirectMatchBanners(),
      testStateMentionRoleMatchBanners(),
      testStateMutedSuppressesUnmentioned(),
      testStateMutedDeliversSilentOnMention(),
      testStateAllBannerEvenWithoutMention(),
      testStateAllSnoozedDowngradesToSilent(),
      testEveryoneGatedByPermission(),
      testHerePreferredOverEveryone(),
      testServerProvidedMentionRoutes(),

      // Integration layer
      testRealisticBodyRoutes(),
    ]),
});

// MARK: - Fixtures

// 26-char ULIDs (Crockford base32, no I/L/O/U).
const ME_ID = "01HZZZZZZZZZZZZZZZZZZZZZZA";
const UID_A = "01HAAAAAAAAAAAAAAAAAAAAAAA";
const UID_B = "01HBBBBBBBBBBBBBBBBBBBBBBB";
const UID_C = "01HCCCCCCCCCCCCCCCCCCCCCCC";
const ROLE_A = "01HRRRRRRRRRRRRRRRRRRRRRRR";

const ME_CTX: CurrentUserContext = { userId: ME_ID, roleIds: new Set() };
const ME_CTX_WITH_ROLE: CurrentUserContext = {
  userId: ME_ID,
  roleIds: new Set([ROLE_A]),
};

function ctx(
  state: ChannelNotificationContext["state"],
): ChannelNotificationContext {
  return {
    state,
    mutedUntil: null,
    allowEveryoneMention: true,
    isDirectMessage: false,
  };
}

// MARK: - Parser cases

function testParsesUserMention(): TestCase {
  const parsed = parseMentions(`hey <@${UID_A}> ping`);
  const ok =
    parsed.userIds.has(UID_A) &&
    parsed.userIds.size === 1 &&
    parsed.roleIds.size === 0 &&
    !parsed.everyone &&
    !parsed.here;
  return ok
    ? pass("parses user mention")
    : fail(
        "parses user mention",
        `got userIds=${[...parsed.userIds]} roleIds=${[...parsed.roleIds]} everyone=${parsed.everyone} here=${parsed.here}`,
      );
}

function testParsesRoleMention(): TestCase {
  const parsed = parseMentions(`ops <@&${ROLE_A}> please ack`);
  const ok =
    parsed.roleIds.has(ROLE_A) &&
    parsed.roleIds.size === 1 &&
    parsed.userIds.size === 0;
  return ok
    ? pass("parses role mention")
    : fail(
        "parses role mention",
        `got roleIds=${[...parsed.roleIds]} userIds=${[...parsed.userIds]}`,
      );
}

function testRejectsBadULID(): TestCase {
  // ULIDs exclude I, L, O, U. A 26-char string containing 'I' is invalid.
  const badId = "0123456789ABCDEFGHIJKLMNOP"; // contains I, L, O — all forbidden
  const parsed = parseMentions(`ping <@${badId}>`);
  const ok = parsed.userIds.size === 0;
  return ok
    ? pass("rejects malformed ULID")
    : fail("rejects malformed ULID", `accepted ${badId}`);
}

function testEveryoneWordBounded(): TestCase {
  const cases: Array<{ body: string; expect: boolean }> = [
    { body: "hey @everyone listen up", expect: true },
    { body: "@everyone", expect: true },
    { body: "ping @everyone!", expect: true },
    { body: "(@everyone)", expect: true },
    { body: "foo@everyone.example.com", expect: false }, // email — must NOT match
    { body: "@everyonelarge", expect: false }, // word boundary — must NOT match
  ];
  for (const { body, expect } of cases) {
    const got = containsEveryone(body);
    if (got !== expect) {
      return fail(
        "@everyone is word-bounded",
        `body="${body}" expected=${expect} got=${got}`,
      );
    }
  }
  return pass("@everyone is word-bounded");
}

function testEveryoneInEmailNotMatched(): TestCase {
  // Targeted regression for the most commonly-cited false positive.
  const parsed = parseMentions("contact: support@everyone.kaether.fr");
  const ok = !parsed.everyone;
  return ok
    ? pass("support@everyone.example.com not matched")
    : fail(
        "support@everyone.example.com not matched",
        "false positive on email-like string",
      );
}

function testParsesHere(): TestCase {
  const parsed = parseMentions("@here can someone help");
  const ok = parsed.here && !parsed.everyone;
  return ok
    ? pass("parses @here")
    : fail(
        "parses @here",
        `got here=${parsed.here} everyone=${parsed.everyone}`,
      );
}

function testMultipleMentionsDeduplicated(): TestCase {
  const body = `<@${UID_A}> <@${UID_A}> <@${UID_B}> <@&${ROLE_A}>`;
  const parsed = parseMentions(body);
  const usersMatch =
    parsed.userIds.has(UID_A) &&
    parsed.userIds.has(UID_B) &&
    parsed.userIds.size === 2;
  const rolesMatch =
    parsed.roleIds.has(ROLE_A) && parsed.roleIds.size === 1;
  const ok = usersMatch && rolesMatch;
  return ok
    ? pass("duplicate mentions collapse to a set")
    : fail(
        "duplicate mentions collapse to a set",
        `got users=${[...parsed.userIds]} roles=${[...parsed.roleIds]}`,
      );
}

function testEmptyBody(): TestCase {
  const parsed = parseMentions("");
  const ok =
    parsed.userIds.size === 0 &&
    parsed.roleIds.size === 0 &&
    !parsed.everyone &&
    !parsed.here;
  return ok
    ? pass("empty body parses to empty")
    : fail(
        "empty body parses to empty",
        `got userIds=${[...parsed.userIds]} roleIds=${[...parsed.roleIds]} everyone=${parsed.everyone} here=${parsed.here}`,
      );
}

// MARK: - Union cases

function testUnionWithServerList(): TestCase {
  const parsed: ParsedMentions = {
    userIds: new Set([UID_A]),
    roleIds: new Set(),
    everyone: false,
    here: false,
  };
  const merged = unionUserIds([UID_B, UID_C], parsed);
  const ok =
    merged.has(UID_A) &&
    merged.has(UID_B) &&
    merged.has(UID_C) &&
    merged.size === 3;
  return ok
    ? pass("union merges server + local user IDs")
    : fail("union merges server + local user IDs", `got ${[...merged]}`);
}

function testUnionWithNilServerList(): TestCase {
  const parsed: ParsedMentions = {
    userIds: new Set([UID_A]),
    roleIds: new Set(),
    everyone: false,
    here: false,
  };
  const merged = unionUserIds(null, parsed);
  const ok = merged.has(UID_A) && merged.size === 1;
  return ok
    ? pass("union with nil server list returns parsed")
    : fail("union with nil server list returns parsed", `got ${[...merged]}`);
}

// MARK: - Router cases

function testSelfAuthoredSuppressed(): TestCase {
  // Authoring a message that mentions yourself must not notify yourself.
  const msg: ParsedMessage = {
    authorId: ME_ID,
    body: `<@${ME_ID}> note`,
    serverProvidedMentions: [ME_ID],
  };
  const dec = route(msg, ME_CTX, ctx("all"));
  const ok =
    dec.kind === "suppress" && dec.reason === "authoredBySelf";
  return ok
    ? pass("self-authored is suppressed even with self mention")
    : fail(
        "self-authored is suppressed even with self mention",
        `got ${JSON.stringify(dec)}`,
      );
}

function testStateNoneSuppresses(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: `<@${ME_ID}> hi`,
    serverProvidedMentions: [ME_ID],
  };
  const dec = route(msg, ME_CTX, ctx("none"));
  const ok = dec.kind === "suppress" && dec.reason === "stateNone";
  return ok
    ? pass(".none suppresses even direct mentions")
    : fail(".none suppresses even direct mentions", `got ${JSON.stringify(dec)}`);
}

function testStateMentionRequiresMention(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: "general chat",
    serverProvidedMentions: [],
  };
  const dec = route(msg, ME_CTX, ctx("mention"));
  const ok =
    dec.kind === "suppress" &&
    dec.reason === "stateMentionRequiresMention";
  return ok
    ? pass(".mention without target suppresses")
    : fail(".mention without target suppresses", `got ${JSON.stringify(dec)}`);
}

function testStateMentionDirectMatchBanners(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: `<@${ME_ID}> ping`,
    serverProvidedMentions: [ME_ID],
  };
  const dec = route(msg, ME_CTX, ctx("mention"));
  const ok =
    dec.kind === "banner" &&
    dec.target !== null &&
    dec.target.kind === "directMention";
  return ok
    ? pass(".mention + direct user match banners")
    : fail(
        ".mention + direct user match banners",
        `got ${JSON.stringify(dec)}`,
      );
}

function testStateMentionRoleMatchBanners(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: `<@&${ROLE_A}> please ack`,
    serverProvidedMentions: [],
  };
  const dec = route(msg, ME_CTX_WITH_ROLE, ctx("mention"));
  const ok =
    dec.kind === "banner" &&
    dec.target !== null &&
    dec.target.kind === "roleMention" &&
    dec.target.roleId === ROLE_A;
  return ok
    ? pass(".mention + role membership match banners")
    : fail(
        ".mention + role membership match banners",
        `got ${JSON.stringify(dec)}`,
      );
}

function testStateMutedSuppressesUnmentioned(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: "small talk",
    serverProvidedMentions: [],
  };
  const dec = route(msg, ME_CTX, ctx("muted"));
  const ok =
    dec.kind === "suppress" && dec.reason === "stateMutedNoMention";
  return ok
    ? pass(".muted suppresses unmentioned")
    : fail(".muted suppresses unmentioned", `got ${JSON.stringify(dec)}`);
}

function testStateMutedDeliversSilentOnMention(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: `<@${ME_ID}> urgent`,
    serverProvidedMentions: [ME_ID],
  };
  const dec = route(msg, ME_CTX, ctx("muted"));
  const ok =
    dec.kind === "silent" && dec.target.kind === "directMention";
  return ok
    ? pass(".muted + mention => silent badge bypass")
    : fail(
        ".muted + mention => silent badge bypass",
        `got ${JSON.stringify(dec)}`,
      );
}

function testStateAllBannerEvenWithoutMention(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: "general chat",
    serverProvidedMentions: [],
  };
  const dec = route(msg, ME_CTX, ctx("all"));
  const ok = dec.kind === "banner" && dec.target === null;
  return ok
    ? pass(".all banners every message")
    : fail(".all banners every message", `got ${JSON.stringify(dec)}`);
}

function testStateAllSnoozedDowngradesToSilent(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: `<@${ME_ID}> ping`,
    serverProvidedMentions: [ME_ID],
  };
  const futureMs = Date.now() + 60 * 60 * 1000; // 1 hour from now
  const chan: ChannelNotificationContext = {
    state: "all",
    mutedUntil: futureMs,
    allowEveryoneMention: true,
    isDirectMessage: false,
  };
  const dec = route(msg, ME_CTX, chan, Date.now());
  const ok =
    dec.kind === "silent" && dec.target.kind === "directMention";
  return ok
    ? pass(".all + active snooze => silent")
    : fail(".all + active snooze => silent", `got ${JSON.stringify(dec)}`);
}

function testEveryoneGatedByPermission(): TestCase {
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: "@everyone ship it",
    serverProvidedMentions: [],
  };
  const withPerm: ChannelNotificationContext = {
    state: "mention",
    mutedUntil: null,
    allowEveryoneMention: true,
    isDirectMessage: false,
  };
  const withoutPerm: ChannelNotificationContext = {
    state: "mention",
    mutedUntil: null,
    allowEveryoneMention: false,
    isDirectMessage: false,
  };
  const decWith = route(msg, ME_CTX, withPerm);
  const decWithout = route(msg, ME_CTX, withoutPerm);
  const ok =
    decWith.kind === "banner" &&
    decWith.target !== null &&
    decWith.target.kind === "everyoneMention" &&
    decWithout.kind === "suppress" &&
    decWithout.reason === "stateMentionRequiresMention";
  return ok
    ? pass("@everyone is gated by allowEveryoneMention")
    : fail(
        "@everyone is gated by allowEveryoneMention",
        `withPerm=${JSON.stringify(decWith)} withoutPerm=${JSON.stringify(decWithout)}`,
      );
}

function testHerePreferredOverEveryone(): TestCase {
  // Body has both. @here is more specific so router should pick it.
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: "@here @everyone status?",
    serverProvidedMentions: [],
  };
  const chan: ChannelNotificationContext = {
    state: "mention",
    mutedUntil: null,
    allowEveryoneMention: true,
    isDirectMessage: false,
  };
  const dec = route(msg, ME_CTX, chan);
  const ok =
    dec.kind === "banner" &&
    dec.target !== null &&
    dec.target.kind === "hereMention";
  return ok
    ? pass("@here preferred over @everyone")
    : fail("@here preferred over @everyone", `got ${JSON.stringify(dec)}`);
}

function testServerProvidedMentionRoutes(): TestCase {
  // Body doesn't textually mention me, but server says I'm mentioned.
  // The receiver should still banner — the server's list is part of
  // the authoritative input in non-E2EE deployments.
  const msg: ParsedMessage = {
    authorId: UID_A,
    body: "see attachment",
    serverProvidedMentions: [ME_ID],
  };
  const dec = route(msg, ME_CTX, ctx("mention"));
  const ok =
    dec.kind === "banner" &&
    dec.target !== null &&
    dec.target.kind === "directMention";
  return ok
    ? pass("server-provided mention triggers banner")
    : fail(
        "server-provided mention triggers banner",
        `got ${JSON.stringify(dec)}`,
      );
}

// MARK: - Integration case

function testRealisticBodyRoutes(): TestCase {
  // Multi-token body that exercises union + role + everyone gate in one go.
  const body = `<@${UID_B}> ping <@&${ROLE_A}> and @everyone fyi`;
  const msg: ParsedMessage = {
    authorId: UID_A,
    body,
    serverProvidedMentions: [UID_B],
  };
  // I am not directly mentioned (UID_B is). I'm in ROLE_A. Everyone is permitted.
  const chan: ChannelNotificationContext = {
    state: "mention",
    mutedUntil: null,
    allowEveryoneMention: true,
    isDirectMessage: false,
  };
  const dec = route(msg, ME_CTX_WITH_ROLE, chan);
  // Role beats @everyone in priority.
  const ok =
    dec.kind === "banner" &&
    dec.target !== null &&
    dec.target.kind === "roleMention" &&
    dec.target.roleId === ROLE_A;
  return ok
    ? pass("realistic body picks role over @everyone")
    : fail(
        "realistic body picks role over @everyone",
        `got ${JSON.stringify(dec)}`,
      );
}
